/**
 * Archiving a single CLAIM — OR-1431.
 *
 * The row menu on Loss Runs archives the claim itself, never the facility: a
 * facility is a one-to-many relation over claims, so excluding it is a
 * different (and much broader) action, and it belongs to Schedule, which owns
 * facilities.
 *
 * There is no per-claim exclusion flag in the backend, so this rides on
 * `status` — see `ARCHIVED_CLAIM_STATUS`. That field already holds the
 * carrier's real claim status, which archiving overwrites. The previous value
 * is kept here so Restore can put it back within the session; after a reload
 * it's gone and Restore clears the field instead. A real `is_excluded` on
 * `submission_loss_run` would remove the need for all of this.
 */
import { isArchivedLocationRef } from "../../../domain/facility-archive";
import type { Facility } from "../../../types/facility";
import { ARCHIVED_CLAIM_STATUS, type LossRunRow, type LossRunStatus } from "../../../types/loss-run";

/** Claim id → the status it had before being archived. Module-level, like the
 *  facility archive counters: the loss-runs store is itself a module-level
 *  pubsub, and row ids are unique across submissions. */
const statusBeforeArchive = new Map<string, LossRunStatus>();

export function isArchivedClaim(row: Pick<LossRunRow, "status">): boolean {
    return row.status === ARCHIVED_CLAIM_STATUS;
}

/** Remember what to restore, then hand back the value to write. */
export function noteStatusBeforeArchive(id: string, status: LossRunStatus): typeof ARCHIVED_CLAIM_STATUS {
    if (status !== ARCHIVED_CLAIM_STATUS) statusBeforeArchive.set(id, status);
    return ARCHIVED_CLAIM_STATUS;
}

/** The status to write on Restore: whatever the claim had before, or empty when
 *  the archive happened in an earlier session and nothing was recorded. */
export function statusToRestore(id: string): LossRunStatus {
    const previous = statusBeforeArchive.get(id) ?? "";
    statusBeforeArchive.delete(id);
    return previous;
}

/** Test seam. */
export function resetClaimArchiveForTests(): void {
    statusBeforeArchive.clear();
}

/**
 * Whether a claim should be dropped from every FE-derived Loss Runs total
 * (summary cards + table footer) — its own status is Archived, or it points
 * at a facility that is (OR-1431). Shared so the cards and the footer can't
 * drift apart from each other or from the backend's own exclusion.
 */
export function isExcludedFromLossRunTotals(
    facilities: Facility[],
    row: Pick<LossRunRow, "status" | "locationId" | "location">,
): boolean {
    return isArchivedClaim(row) || isArchivedLocationRef(facilities, row);
}
